import clsx from "clsx";
import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { ALL_TIME_DATA } from "components/constants";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { Listbox } from "mds/components/Listbox";
import { Tooltip } from "mds/components/Tooltip";
import { TooltipTrigger } from "mds/components/TooltipTrigger";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { ArrowDownIcon, ArrowDownToLineIcon, ArrowUpDownIcon, ArrowUpIcon } from "mds/icons";
import { CalendarIcon } from "mds/icons/Calendar";
import { toastErrorMessage } from "utils/alerts";
import { type CSVData, downloadCSVFile } from "utils/file";

export type SortDirection = "asc" | "desc";

export type SortableTableDateRangeOption = {
  id: string;
  title: string;
  value: number;
};

type SortableTableContextValue<TData, TField extends string> = {
  sortField: TField | null;
  sortDirection: SortDirection;
  handleSort: (field: TField) => void;
  data: TData[];
  sortedData: TData[];
  getSortValue: (data: TData, field: TField) => number | string | boolean | null;
};

const SortableTableContext = createContext<SortableTableContextValue<unknown, string> | undefined>(
  undefined,
);

function useSortableTableContext<TData, TField extends string>() {
  const context = useContext(SortableTableContext);
  if (!context) {
    throw new Error(
      "SortableHeader, SortableBody, and SortableTableExportButton must be used within SortableTable",
    );
  }
  return context as SortableTableContextValue<TData, TField>;
}

export type SortableTableProps<TData, TField extends string> = {
  data: TData[];
  getSortValue: (data: TData, field: TField) => number | string | boolean | null;
  defaultSortField?: TField;
  defaultSortDirection?: SortDirection;
  className?: string;
  children: ReactNode;
};

export type SortableTableWrapperProps<TData, TField extends string> = {
  data: TData[];
  getSortValue: (data: TData, field: TField) => number | string | boolean | null;
  defaultSortField?: TField;
  defaultSortDirection?: SortDirection;
  className?: string;
  children: ReactNode;
};

export function SortableTableWrapper<TData, TField extends string>({
  data,
  getSortValue,
  defaultSortField,
  defaultSortDirection = "asc",
  className,
  children,
}: SortableTableWrapperProps<TData, TField>) {
  const [sortField, setSortField] = useState<TField | null>(defaultSortField ?? null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(defaultSortDirection);

  const handleSort = useCallback(
    (field: TField) => {
      if (sortField === field) {
        setSortDirection(sortDirection === "asc" ? "desc" : "asc");
      } else {
        setSortField(field);
        setSortDirection("asc");
      }
    },
    [sortField, sortDirection],
  );

  const sortedData = useMemo(() => {
    if (!sortField) return data;

    return [...data].sort((a, b) => {
      const aValue = getSortValue(a, sortField);
      const bValue = getSortValue(b, sortField);

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      // Sort based on value type
      let comparison = 0;
      if (typeof aValue === "string" && typeof bValue === "string") {
        comparison = aValue.localeCompare(bValue);
      } else if (typeof aValue === "number" && typeof bValue === "number") {
        comparison = aValue - bValue;
      } else if (typeof aValue === "boolean" && typeof bValue === "boolean") {
        comparison = aValue === bValue ? 0 : aValue ? 1 : -1;
      }

      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [data, sortField, sortDirection, getSortValue]);

  const contextValue = useMemo(
    () => ({
      sortField,
      sortDirection,
      handleSort,
      data,
      sortedData,
      getSortValue,
    }),
    [sortField, sortDirection, handleSort, data, sortedData, getSortValue],
  );

  return (
    <SortableTableContext.Provider value={contextValue}>
      <div className={className}>{children}</div>
    </SortableTableContext.Provider>
  );
}

export function SortableTable({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return <table className={clsx("mds-table", className)}>{children}</table>;
}

export type SortableHeaderProps<TField extends string> = {
  field: TField;
  label: string;
  tooltip?: string;
  sortable?: boolean;
  className?: string;
};

function SortHeaderIcon<TField extends string>({
  field,
  sortField,
  sortDirection,
}: {
  field: TField;
  sortField: TField | null;
  sortDirection: SortDirection;
}) {
  if (sortField !== field) {
    return <ArrowUpDownIcon size="xs" />;
  }
  return sortDirection === "asc" ? <ArrowUpIcon size="xs" /> : <ArrowDownIcon size="xs" />;
}

export function SortableHeader<TField extends string>({
  field,
  label,
  tooltip,
  sortable = true,
  className,
}: SortableHeaderProps<TField>) {
  const { handleSort, sortField, sortDirection } = useSortableTableContext<unknown, TField>();

  if (!sortable) {
    return (
      <th className={clsx("w-16", className)}>
        {tooltip ? (
          <TooltipTrigger content={tooltip} place="top">
            <span className="text-black-tint-20">{label}</span>
          </TooltipTrigger>
        ) : (
          <span className="text-black-tint-20">{label}</span>
        )}
      </th>
    );
  }

  const button = (
    <button
      className="flex w-full cursor-pointer items-center justify-start gap-1 border-0 bg-transparent p-0 text-left font-[inherit] text-black-tint-20 hover:text-blue"
      type="button"
      onClick={() => handleSort(field)}
    >
      {label}
      <SortHeaderIcon field={field} sortDirection={sortDirection} sortField={sortField} />
    </button>
  );

  return (
    <th className={clsx("w-16", className)}>
      {tooltip ? (
        <Tooltip content={tooltip} place="top">
          {button}
        </Tooltip>
      ) : (
        button
      )}
    </th>
  );
}

export type SortableBodyProps<TData> = {
  children: (sortedData: TData[]) => ReactNode;
  emptyMessage?: string;
  emptyColSpan?: number;
  className?: string;
};

export function SortableBody<TData>({
  children,
  emptyMessage,
  emptyColSpan,
  className,
}: SortableBodyProps<TData>) {
  const { sortedData } = useSortableTableContext<TData, string>();

  return (
    <tbody className={clsx("body-xs", className)}>
      {sortedData.length === 0 && emptyMessage ? (
        <tr>
          <td className="text-left" colSpan={emptyColSpan ?? 100}>
            {emptyMessage}
          </td>
        </tr>
      ) : (
        children(sortedData)
      )}
    </tbody>
  );
}

const DATE_RANGE_OPTIONS: SortableTableDateRangeOption[] = [
  { id: "7", title: t("common.date_range.last_7_days"), value: 7 },
  { id: "30", title: t("common.date_range.last_30_days"), value: 30 },
  { id: "all", title: t("common.date_range.all_time"), value: ALL_TIME_DATA },
];

export type SortableTableDateRangeProps = {
  value: SortableTableDateRangeOption | null;
  onChange: (option: SortableTableDateRangeOption) => void;
  className?: string;
};

export function SortableTableDateRange({
  value,
  onChange,
  className,
}: SortableTableDateRangeProps) {
  useEffect(() => {
    if (!value) {
      const allTimeOption = DATE_RANGE_OPTIONS.find((option) => option.value === ALL_TIME_DATA);
      if (allTimeOption) {
        // react-doctor-disable-next-line react-doctor/no-pass-data-to-parent -- sets default date range when parent passes null
        onChange(allTimeOption);
      }
    }
    // eslint-disable-next-line @minervaproject/no-object-deps/no-object-deps
  }, [value, onChange]);
  return (
    <Listbox
      buttonSize="s"
      buttonText={<IconText iconStart={<CalendarIcon />} text={value?.title ?? ""} />}
      className={className}
      items={DATE_RANGE_OPTIONS}
      selectedValue={value?.id}
      onSelectionChange={(item) => onChange(item)}
    />
  );
}

export type SortableTableExportButtonProps<TData> = {
  generateCSV: (sortedData: TData[]) => { csvData: CSVData; filename: string };
  disabledMessage?: string;
  errorMessage?: string;
  buttonText?: string;
  className?: string;
  disabled?: boolean;
};

export function SortableTableExportButton<TData>({
  generateCSV,
  disabledMessage,
  errorMessage,
  buttonText,
  className,
  disabled: externalDisabled,
}: SortableTableExportButtonProps<TData>) {
  const { sortedData } = useSortableTableContext<TData, string>();

  const handleExport = useCallback(() => {
    if (!sortedData || sortedData.length === 0) {
      if (disabledMessage) {
        toastErrorMessage(disabledMessage);
      }
      return;
    }

    try {
      const { csvData, filename } = generateCSV(sortedData);
      downloadCSVFile(csvData, filename);
    } catch {
      toastErrorMessage(errorMessage ?? "Failed to export data");
    }
  }, [sortedData, generateCSV, disabledMessage, errorMessage]);

  const isDisabled = externalDisabled ?? (!sortedData || sortedData.length === 0);

  return (
    <SquareButton
      className={className}
      disabled={isDisabled}
      kind="tertiary"
      size="s"
      onClick={handleExport}
    >
      <IconText iconStart={<ArrowDownToLineIcon />} text={buttonText ?? "Export"} />
    </SquareButton>
  );
}
